import React, { useEffect, useMemo, useState } from "react";
import { Box, Typography, Alert } from "@mui/material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";

import { useQuizContext } from "@/state/QuizContext";
import { useDogInclusion } from "@/state/useDogInclusion";
import { isDogIncluded, dogPossessive } from "@/helpers/dogInclusion";
import { writeResultsStepToURL } from "@/state/urlState";
import { InclusionSwitch } from "../plan/WhoIsInBoxBlock";
import { useProducts } from "@/state/ProductsContext";
import { SELLING_PLAN_LABELS, SellingPlanKey } from "@/helpers/plansToPacks";
import { PawsDayPicker } from "../deliveryDates/PawsDayPicker";
import { buildHiddenPriceMap } from "../hiddenPlanPricing";
import { fromYmd, computeShipDate, toYmd, isDisabledDate, findNextAllowedDate, lookupShipDate, findFirstAvailableFromApi } from "../deliveryDates/datesHelper";
import { fetchAvailableDatesWithConfig, type QivoraDate } from "@/api/qivoraClient";
import { QUIZ } from "@/theme/quizTokens";
import {
  useExperimentValue,
  QUIZ_PRESENTATION_EXPERIMENT_KEY,
} from "@/experiments/useExperimentValue";
import {
  ReviewHeader,
  MoneyBackBanner,
  FrozenBand,
  WhatYouSayCard,
  OurPromiseBand,
  BowlsLickedBand,
} from "../review/ReviewSections";

const PLAN_KEY_BY_DAYS: Record<number, SellingPlanKey> = {
  7: "DAY_7",
  14: "DAY_14",
  28: "DAY_28",
};

type VariantMeta = {
  title: string;
  image: string;
  grams: number;
  price: number;
};

const BASE_DISCOUNT = 0.25;
const getDiscountMultiplier = () => 1 - BASE_DISCOUNT;

type PlanDays = 7 | 14 | 28;
type PackSize = 250 | 400 | 600 | 800;

/** One recipe/treat line row in the order list (Figma 16v2). */
function OrderLineRow({
  image,
  title,
  grams,
  qty,
}: {
  image?: string;
  title: string;
  grams?: number;
  qty: number;
}) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "14px",
        py: "14px",
        borderBottom: "1px solid #e5ddd2",
      }}
    >
      {image && (
        <Box
          component="img"
          src={image}
          alt={title}
          sx={{
            width: 48,
            height: 48,
            borderRadius: "10px",
            objectFit: "cover",
            flexShrink: 0,
          }}
        />
      )}
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography
          sx={{
            fontFamily: QUIZ.font.body,
            fontWeight: 700,
            fontSize: "15px",
            color: QUIZ.color.ink,
            lineHeight: 1.25,
          }}
        >
          {title}
        </Typography>
        {grams ? (
          <Typography sx={{ fontFamily: QUIZ.font.body, fontSize: "13px", color: "#9098a3", mt: "2px" }}>
            {grams}g
          </Typography>
        ) : null}
      </Box>
      <Typography
        sx={{
          fontFamily: QUIZ.font.body,
          fontWeight: 700,
          fontSize: "15px",
          color: QUIZ.color.ink,
          flexShrink: 0,
        }}
      >
        x{qty}
      </Typography>
    </Box>
  );
}

export default function ResultsReview() {
  const { state, dispatch } = useQuizContext();

  // Experiment lever (presentation only, default = current copy).
  const savingsLabel = useExperimentValue(
    QUIZ_PRESENTATION_EXPERIMENT_KEY,
    "savings_label",
    "Total Saving"
  );
  const { includedCount, setDogIncluded } = useDogInclusion();
  const {
    planProducts,
    mealProducts,
    treatProducts,
    hiddenPriceProducts,
  } = useProducts();

  const dogs = useMemo(() =>
    state.dogOrder.map((id) => ({
      id,
      form: state.dogForms[id] ?? {},
    })),
    [state.dogOrder, state.dogForms]
  );

  const formatPrice = (pounds: number) => `£${(pounds || 0).toFixed(2)}`;

  const isLoading = !planProducts.length || !mealProducts.length;

  // ✅ Hidden pricing map (planDays -> packSize -> pricePerPack)
  const hiddenPriceMap = useMemo(() => {
    return buildHiddenPriceMap(hiddenPriceProducts ?? []);
  }, [hiddenPriceProducts]);

  // Track pricing errors to show user-facing messages
  const [pricingErrors, setPricingErrors] = useState<string[]>([]);

  // Track delivery date errors
  const [dateError, setDateError] = useState<string | null>(null);

  // Qivora dates
  const PAWS_SHOP = "kfuag1-zp.myshopify.com";
  const [qivoraDates, setQivoraDates] = useState<QivoraDate[] | null>(null);
  const [deliveryDayNames, setDeliveryDayNames] = useState<string[]>([]);
  const [cutoffHours, setCutoffHours] = useState<number>(36);
  const [datesLoading, setDatesLoading] = useState(true);

  useEffect(() => {
    fetchAvailableDatesWithConfig(PAWS_SHOP)
      .then((res) => {
        setQivoraDates(res.dates);
        setDeliveryDayNames(res.allowed_delivery_names);
        setCutoffHours(res.cutoff_hours);
      })
      .catch(() => setQivoraDates(null))
      .finally(() => setDatesLoading(false));
  }, []);

  // Validate pricing data and collect errors
  useEffect(() => {
    if (isLoading) return;

    const errors: string[] = [];

    for (const dog of dogs) {
      // Issue #50: an excluded dog isn't in the box — its plan config must
      // never block checkout.
      if (!isDogIncluded(dog.form)) continue;
      const dogName = dog.form?.name || "Your dog";
      const planDays = Number(dog.form?.selectedPlanDays);
      const packSize = Number(dog.form?.selectedPackSize);
      const mealSel = (dog.form?.mealSelection ?? []) as { variantId: string; count: number }[];
      const hasMeals = mealSel.some(item => item?.count > 0);

      if (hasMeals) {
        if (!planDays || ![7, 14, 28].includes(planDays)) {
          errors.push(`${dogName}: Please select a valid plan duration.`);
        } else if (!packSize || ![250, 400, 600, 800].includes(packSize)) {
          errors.push(`${dogName}: Please select a valid pack size.`);
        } else {
          const pricePerPack = Number(hiddenPriceMap?.[planDays as 7 | 14 | 28]?.[packSize as 250 | 400 | 600 | 800] ?? 0);
          if (!pricePerPack || pricePerPack <= 0) {
            errors.push(`${dogName}: Unable to calculate price for ${packSize}g / ${planDays} day plan. Please try a different option.`);
          }
        }
      }
    }

    setPricingErrors(errors);
  }, [dogs, hiddenPriceMap, isLoading]);

  // Build variant lookup (variantId → product data) from meals + treats + Christmas specials
  const variantLookup: Record<string, VariantMeta> = useMemo(() => {
    const out: Record<string, VariantMeta> = {};

    const allProducts = [
      ...(mealProducts ?? []),
      ...(treatProducts ?? []),
    ];

    for (const product of allProducts) {
      for (const variant of product.variants ?? []) {
        const gramsFromTitle = parseInt(String(variant.title), 10);
        const grams =
          variant.grams && Number(variant.grams) > 0
            ? Number(variant.grams)
            : Number.isFinite(gramsFromTitle)
              ? gramsFromTitle
              : 0;

        out[String(variant.id)] = {
          title: String(product.title ?? ""),
          image: Array.isArray(product.image)
            ? (product.image[0] as string)
            : (product.image as string),
          grams,
          price: Number(variant.price),
        };
      }
    }

    return out;
  }, [mealProducts, treatProducts]);

  // Auto-select / validate the delivery date (must run on every render pass — before
  // any early return — to satisfy the rules of hooks).
  useEffect(() => {
    // Wait for Qivora dates to load before auto-selecting
    if (datesLoading) return;

    const raw = state.deliveryDate;
    const now = new Date();

    // Helper: pick first available date (Qivora API or hardcoded fallback)
    const pickNext = (): { ymd: string; shipYmd: string } | null => {
      if (qivoraDates?.length) {
        const first = findFirstAvailableFromApi(qivoraDates);
        if (first) return { ymd: first.date, shipYmd: first.ship_date };
      }
      const next = findNextAllowedDate(now);
      if (next) return { ymd: toYmd(next), shipYmd: toYmd(computeShipDate(next)) };
      return null;
    };

    // Helper: check if a date is valid in the current set
    const isValid = (dateObj: Date): boolean => {
      if (qivoraDates) {
        const ymd = toYmd(dateObj);
        return qivoraDates.some((d) => d.date === ymd);
      }
      return !isDisabledDate(dateObj, now);
    };

    // Case 1: No delivery date set yet - auto-select next available
    if (!raw) {
      const next = pickNext();
      if (!next) {
        setDateError("No delivery dates available. Please contact support.");
        return;
      }
      setDateError(null);
      dispatch({ type: "SET_DELIVERY_DATES", deliveryDate: next.ymd, shipDate: next.shipYmd });
      return;
    }

    // Case 2: Date string is malformed/corrupt
    const selected = fromYmd(raw);
    if (!selected) {
      const next = pickNext();
      if (!next) {
        setDateError("No delivery dates available. Please contact support.");
        return;
      }
      setDateError(null);
      dispatch({ type: "SET_DELIVERY_DATES", deliveryDate: next.ymd, shipDate: next.shipYmd });
      return;
    }

    // Case 3: Selected date is no longer valid
    if (!isValid(selected)) {
      const next = pickNext();
      if (!next) {
        setDateError("No delivery dates available. Please contact support.");
        return;
      }
      setDateError(null);
      dispatch({ type: "SET_DELIVERY_DATES", deliveryDate: next.ymd, shipDate: next.shipYmd });
      return;
    }

    // Case 4: Date is valid but ship date might need updating from Qivora
    if (qivoraDates) {
      const apiShip = lookupShipDate(qivoraDates, raw);
      if (apiShip && apiShip !== state.shipDate) {
        dispatch({ type: "SET_DELIVERY_DATES", deliveryDate: raw, shipDate: apiShip });
      }
    }

    setDateError(null);
  }, [state.deliveryDate, dispatch, datesLoading, qivoraDates]);

  if (isLoading) {
    return (
      <Box sx={{ textAlign: "center", mt: 6 }}>
        <Typography>Loading order review...</Typography>
      </Box>
    );
  }

  // CALCULATE TOTALS
  let grandTotalMealsFull = 0; // meals BEFORE intro discount (still includes plan-length pricing)
  let grandTotalDiscountedMeals = 0; // meals AFTER intro discount
  let grandTotalTreats = 0; // treats (inc. Christmas special)
  let firstDogPlanDays: number | null = null;

  // Issue #50: jump back to a specific dog's meal step from its review card.
  // The one-shot flag tells the meal step to scroll to this dog on arrival;
  // keying the scroll off activeDogId alone would also fire on every normal
  // Plan → Meals advance, since activeDogId is always backfilled.
  const handleEditMeals = (dogId: string) => {
    try {
      sessionStorage.setItem("paws_scroll_to_dog", dogId);
    } catch { }
    dispatch({ type: "SET_ACTIVE_DOG", dogId });
    dispatch({ type: "SET_RESULTS_STEP", step: 3 });
    writeResultsStepToURL(3);
  };

  const multiDog = dogs.length > 1;

  const dogBlocks = dogs
    .map((dog) => {
      const included = isDogIncluded(dog.form);
      const dogName = dog.form?.name || "Your dog";

      // Issue #50: an excluded dog collapses to a single muted row with the
      // same switch, so adding them back is one tap — never a dead end.
      if (!included) {
        const poss = dogPossessive(dog.form?.sex);
        return (
          <Box
            key={dog.id}
            sx={{
              mb: "8px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "10px",
              bgcolor: QUIZ.color.cream2,
              borderRadius: "12px",
              px: "16px",
              py: "13px",
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  fontFamily: QUIZ.font.body,
                  fontWeight: 700,
                  fontSize: "17px",
                  color: QUIZ.color.grey,
                  lineHeight: 1.2,
                }}
              >
                {dogName}
              </Typography>
              <Typography
                sx={{
                  fontFamily: QUIZ.font.body,
                  fontSize: "12.5px",
                  color: QUIZ.color.statLabel,
                  mt: "3px",
                }}
              >
                Not in this box. Click the toggle to add {poss} meals back in.
              </Typography>
            </Box>
            <InclusionSwitch
              included={false}
              onDark={false}
              label={`${dogName} included in this box`}
              onToggle={(next) => setDogIncluded(dog.id, next)}
            />
          </Box>
        );
      }

      const mealSel = (dog.form?.mealSelection ?? []) as {
        variantId: string;
        count: number;
      }[];

      const dogTreats =
        (state.treatSelections && state.treatSelections[dog.id]) || {};

      const hasMeals = mealSel.length > 0;
      const hasTreats =
        dogTreats && Object.values(dogTreats).some((count) => Number(count) > 0);

      if (!hasMeals && !hasTreats) return null;

      const selectedPlanDays = dog.form?.selectedPlanDays
        ? Number(dog.form.selectedPlanDays)
        : undefined;

      const selectedPackSize = dog.form?.selectedPackSize
        ? Number(dog.form.selectedPackSize)
        : undefined;

      const planDays = ([7, 14, 28].includes(Number(selectedPlanDays))
        ? Number(selectedPlanDays)
        : null) as PlanDays | null;

      const packSize = ([250, 400, 600, 800].includes(Number(selectedPackSize))
        ? Number(selectedPackSize)
        : null) as PackSize | null;

      if (firstDogPlanDays == null && planDays) {
        firstDogPlanDays = planDays;
      }

      const planKey =
        planDays && PLAN_KEY_BY_DAYS[planDays]
          ? PLAN_KEY_BY_DAYS[planDays]
          : undefined;

      const planLabel = planKey ? SELLING_PLAN_LABELS[planKey] : undefined;

      // ---- separate base totals: meals vs treats ----
      let dogMealsFull = 0; // meals BEFORE intro discount
      let dogTreatsFull = 0; // treats (and christmas) - no intro discount applied here

      // ✅ Use hidden product price-per-pack for meals (not meal variant.price)
      const pricePerPack =
        planDays && packSize
          ? Number(hiddenPriceMap?.[planDays]?.[packSize] ?? 0)
          : 0;

      /* ---------------- MEAL LINE ROWS ---------------- */
      const mealRows = mealSel
        .filter((item) => item && item.variantId && item.count > 0)
        .map((item) => {
          const meta = variantLookup[item.variantId];
          if (!meta) return null;

          const lineFull = pricePerPack * item.count;
          dogMealsFull += lineFull;

          return (
            <OrderLineRow
              key={`meal-${item.variantId}`}
              image={meta.image}
              title={meta.title}
              grams={meta.grams}
              qty={item.count}
            />
          );
        })
        .filter(Boolean);

      /* ---------------- TREAT LINE ROWS ---------------- */
      const treatRows = Object.entries(dogTreats)
        .filter(([_, count]) => Number(count) > 0)
        .map(([variantId, count]) => {
          const qty = Number(count);
          const meta = variantLookup[variantId];

          const lineTotal = meta ? meta.price * qty : 0;
          dogTreatsFull += lineTotal;

          const title = meta ? meta.title : `Treat (${variantId})`;

          return (
            <OrderLineRow
              key={`treat-${variantId}`}
              image={meta?.image}
              title={title}
              grams={meta?.grams}
              qty={qty}
            />
          );
        });

      if (!mealRows.length && !treatRows.length) return null;

      // ✅ Apply ONLY the intro discount
      const discountMultiplier = getDiscountMultiplier();

      const dogPlanFull = dogMealsFull; // BEFORE intro discount
      const dogPlanTotal = dogMealsFull * discountMultiplier; // AFTER intro discount

      const dogTreatsTotal = dogTreatsFull;

      const dogTotalFull = dogPlanFull + dogTreatsTotal;
      const dogTotal = dogPlanTotal + dogTreatsTotal;

      // accumulate totals
      grandTotalMealsFull += dogPlanFull;
      grandTotalDiscountedMeals += dogPlanTotal;
      grandTotalTreats += dogTreatsTotal;

      const planLine = [
        planLabel ? `${planDays} day plan` : null,
        packSize ? `${packSize}g` : null,
      ]
        .filter(Boolean)
        .join(" – ");

      return (
        <Box key={dog.id} sx={{ mb: "8px" }}>
          {/* Dog header card */}
          <Box
            sx={{
              bgcolor: "#fff",
              borderRadius: "14px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
              px: "18px",
              py: "14px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              gap: 2,
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  fontFamily: QUIZ.font.body,
                  fontWeight: 700,
                  fontSize: "19px",
                  color: QUIZ.color.ink,
                  lineHeight: 1.2,
                }}
              >
                {dog.form?.name || "Your dog"}
              </Typography>
              {planLine && (
                <Typography
                  sx={{
                    fontFamily: QUIZ.font.body,
                    fontSize: "15px",
                    color: "#9098a3",
                    mt: "2px",
                  }}
                >
                  {planLine}
                </Typography>
              )}
            </Box>

            <Box sx={{ textAlign: "right", flexShrink: 0 }}>
              <Typography
                sx={{
                  fontFamily: QUIZ.font.body,
                  fontWeight: 800,
                  fontSize: "22px",
                  color: QUIZ.color.ink,
                  lineHeight: 1.1,
                  whiteSpace: "nowrap",
                }}
              >
                {formatPrice(dogTotal)}
              </Typography>
              {dogTotalFull > dogTotal && (
                <Typography
                  sx={{
                    fontFamily: QUIZ.font.body,
                    fontSize: "14px",
                    color: "#f2695c",
                    textDecoration: "line-through",
                    fontWeight: 700,
                    whiteSpace: "nowrap",
                    mt: "2px",
                  }}
                >
                  {formatPrice(dogTotalFull)}
                </Typography>
              )}
            </Box>
          </Box>

          {/* Issue #50: per-dog controls — edit meals + include switch */}
          {multiDog && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "10px",
                px: "4px",
                pt: "11px",
                pb: "2px",
                borderBottom: "1px solid #e5ddd2",
              }}
            >
              <Box
                role="button"
                tabIndex={0}
                onClick={() => handleEditMeals(dog.id)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleEditMeals(dog.id);
                  }
                }}
                sx={{
                  fontFamily: QUIZ.font.body,
                  fontWeight: 700,
                  fontSize: "13.5px",
                  color: QUIZ.color.brown,
                  textDecoration: "underline",
                  cursor: "pointer",
                  userSelect: "none",
                  "&:hover": { opacity: 0.75 },
                }}
              >
                Edit {dogName}'s meals
              </Box>
              <InclusionSwitch
                included
                disabled={includedCount <= 1}
                onDark={false}
                label={`${dogName} included in this box`}
                onToggle={(next) => setDogIncluded(dog.id, next)}
              />
            </Box>
          )}

          {/* Line rows */}
          <Box sx={{ px: "4px" }}>
            {mealRows}
            {treatRows}
          </Box>
        </Box>
      );
    })
    .filter(Boolean);

  const TOTAL_MEALS_FULL = grandTotalMealsFull;
  const TOTAL_MEALS = grandTotalDiscountedMeals;
  const TOTAL_TREATS = grandTotalTreats;

  const TOTAL_FULL = TOTAL_MEALS_FULL + TOTAL_TREATS;
  const TOTAL = TOTAL_MEALS + TOTAL_TREATS;

  const TOTAL_SAVINGS = Math.max(0, TOTAL_FULL - TOTAL);
  void TOTAL_MEALS; // used in totals; kept for clarity

  const perDay = firstDogPlanDays ? TOTAL / firstDogPlanDays : null;

  return (
    <Box sx={{ pb: { xs: 0, md: 0 } }}>
      {/* REVIEW heading + chip */}
      <ReviewHeader />

      {/* Pricing error banner (functional state, kept from the previous design) */}
      {pricingErrors.length > 0 && (
        <Alert
          severity="error"
          sx={{
            mb: 2,
            borderRadius: "12px",
            maxWidth: 760,
            mx: "auto",
            "& .MuiAlert-message": { width: "100%" },
          }}
        >
          <Typography sx={{ fontWeight: 600, mb: 0.5 }}>
            There's an issue with your order
          </Typography>
          {pricingErrors.map((error, idx) => (
            <Typography key={idx} sx={{ fontSize: "14px" }}>
              • {error}
            </Typography>
          ))}
        </Alert>
      )}

      {/* Order details + delivery date — stacked on mobile, side-by-side on desktop */}
      <Box
        sx={{
          maxWidth: 780,
          mx: "auto",
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          gap: { xs: "26px", md: "44px" },
          alignItems: { xs: "stretch", md: "flex-start" },
        }}
      >
        {/* LEFT: order details */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontFamily: QUIZ.font.body,
              fontWeight: 700,
              fontSize: "17px",
              color: QUIZ.color.ink,
              textAlign: { xs: "center", md: "left" },
              mb: "14px",
            }}
          >
            Your Order Details
          </Typography>

          {dogBlocks}

          {/* Totals */}
          <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, mt: "18px" }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  fontFamily: QUIZ.font.body,
                  fontWeight: 700,
                  fontSize: "19px",
                  color: QUIZ.color.ink,
                }}
              >
                Total
              </Typography>
              {TOTAL_SAVINGS > 0 && (
                <Typography
                  sx={{
                    fontFamily: QUIZ.font.body,
                    fontWeight: 600,
                    fontSize: "15px",
                    color: "#9098a3",
                    mt: "4px",
                  }}
                >
                  {savingsLabel} {formatPrice(TOTAL_SAVINGS)}
                </Typography>
              )}
            </Box>

            <Box sx={{ textAlign: "right", flexShrink: 0 }}>
              <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "flex-end", gap: 1 }}>
                <Typography
                  sx={{
                    fontFamily: QUIZ.font.display,
                    fontWeight: 900,
                    fontSize: { xs: "24px", md: "26px" },
                    color: QUIZ.color.brown,
                    whiteSpace: "nowrap",
                    lineHeight: 1,
                  }}
                >
                  {formatPrice(TOTAL)}
                </Typography>
                {TOTAL_FULL > TOTAL && (
                  <Typography
                    sx={{
                      fontFamily: QUIZ.font.body,
                      fontSize: "15px",
                      color: "#f2695c",
                      textDecoration: "line-through",
                      fontWeight: 700,
                      whiteSpace: "nowrap",
                      lineHeight: 1,
                    }}
                  >
                    {formatPrice(TOTAL_FULL)}
                  </Typography>
                )}
              </Box>
              {perDay != null && (
                <Typography
                  sx={{
                    fontFamily: QUIZ.font.body,
                    fontWeight: 600,
                    fontSize: "15px",
                    color: QUIZ.color.ink,
                    mt: "6px",
                  }}
                >
                  {formatPrice(perDay)} per day
                </Typography>
              )}
            </Box>
          </Box>

          {/* Trial / shipping notes */}
          <Box sx={{ mt: "14px" }}>
            {TOTAL_FULL > TOTAL && (
              <Typography sx={{ fontFamily: QUIZ.font.body, fontSize: "13px", color: "#9098a3" }}>
                After your trial, your plan will cost <strong style={{ color: "#3e3e3e" }}>{formatPrice(TOTAL_FULL)}</strong>
              </Typography>
            )}
            <Typography sx={{ fontFamily: QUIZ.font.body, fontSize: "13px", color: "#9098a3", mt: "4px" }}>
              Shipping: Free on your first box (£4.99 after)
            </Typography>
          </Box>

          {/* Money-back guarantee banner */}
          <MoneyBackBanner />
        </Box>

        {/* RIGHT: delivery date card */}
        <Box sx={{ width: { xs: "100%", md: 380 }, flexShrink: 0 }}>
          <Box
            sx={{
              bgcolor: "#3f2b21",
              borderRadius: "18px",
              p: { xs: "20px", md: "22px" },
            }}
          >
            <Typography
              sx={{
                fontFamily: QUIZ.font.body,
                fontWeight: 700,
                fontSize: "17px",
                color: "#fff",
                mb: "10px",
              }}
            >
              Select a delivery date <span style={{ color: "#f2695c" }}>*</span>
            </Typography>

            <Typography
              sx={{
                fontFamily: QUIZ.font.body,
                fontSize: "13.5px",
                lineHeight: 1.45,
                color: "rgba(255,255,255,0.85)",
                mb: "16px",
              }}
            >
              Choose your first delivery. You can manage future deliveries from your
              account. Deliveries take place on <strong>Tuesdays</strong> or <strong>Fridays</strong>.
            </Typography>

            <PawsDayPicker
              value={state.deliveryDate}
              onChange={({ deliveryDate, shipDate }) => {
                dispatch({ type: "SET_DELIVERY_DATES", deliveryDate, shipDate });
              }}
              qivoraDates={qivoraDates}
              loading={datesLoading}
              deliveryDayNames={deliveryDayNames}
              cutoffHours={cutoffHours}
              suppressFooter={!!dateError}
            />

            {/* Invalid-selection strip (Figma 16v2 error state) */}
            {dateError && (
              <Box
                sx={{
                  mt: "12px",
                  bgcolor: "#f2695c",
                  borderRadius: "12px",
                  p: "14px 16px",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: "8px", mb: "6px" }}>
                  <ErrorOutlineIcon sx={{ fontSize: 18, color: "#fff" }} />
                  <Typography
                    sx={{
                      fontFamily: QUIZ.font.body,
                      fontWeight: 600,
                      fontSize: "11px",
                      letterSpacing: "0.8px",
                      textTransform: "uppercase",
                      color: "rgba(255,255,255,0.9)",
                    }}
                  >
                    Invalid selection
                  </Typography>
                </Box>
                <Typography
                  sx={{
                    fontFamily: QUIZ.font.body,
                    fontWeight: 700,
                    fontSize: "16px",
                    lineHeight: 1.3,
                    color: "#fff",
                  }}
                >
                  Date unavailable – please select a new date
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      {/* Marketing sections (full-bleed) */}
      <FrozenBand />
      <Box sx={{ maxWidth: 1030, mx: "auto" }}>
        <WhatYouSayCard />
      </Box>
      <OurPromiseBand />
      <BowlsLickedBand />
    </Box>
  );
}
